import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  generatePairKey,
  newParsingAccount,
  parsingAccountsQuery,
  rebuildConfiguration,
  removeParsingAccount,
  saveParsingAccount,
  saveServices,
  saveSmsPairKey,
  ticketGeneralSettingsQuery,
  type ParsingAccount,
  type RebuildResult,
} from '../../api/settingsEaseedeskTicketSettings'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → General settings.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_settings/index.php` + `parsing_list.php`, si gest cu gest fata de
 * `public/js/views/easeedesk_settings/tickets/ticket_settings/index-1.js` si
 * `ParsingSettings` din `public/js/settings/easeedesk/tickets_settings.js`.
 *
 * "Rebuild configuration" (sus, ca in macheta veche) reface hash-urile
 * Easeedesk din Redis - `RebuildRedisCache` portat in
 * `App\Settings\Easeedesk\Cache\EaseedeskCacheRebuild`. Fara el, un tip de
 * tichet, un flux sau un camp nou scris din v4 nu ajunge la tichetele care se
 * deschid singure (e-mail, agenti) si iese cu celula goala in lista. Spre
 * deosebire de portal, unde prima parte picata oprea tot lantul si omul vedea
 * doar "Something went wrong.", aici fiecare parte merge separat si se spune
 * care n-a mers.
 *
 * CE LIPSESTE FATA DE ECRANUL VECHI, cu buna stiinta:
 *
 * - CKEditor pe corpul mesajului de instiintare. Aici e o casuta obisnuita:
 *   textul se pastreaza asa cum e scris, si se randeaza tot ca HTML.
 *
 * PAROLA CONTULUI: casuta arata masca cand exista deja o parola, la fel ca in
 * macheta veche - dar aici masca inseamna "nu o schimba". In CI3 masca ajungea
 * prin `password_hash()` chiar peste parola adevarata.
 */

/** Un cont de citit e-mail: panoul care se desface, cu formularul lui. */
function ParsingAccountPanel({
  account,
  index,
  onReload,
}: {
  account: ParsingAccount
  index: number
  onReload(): void
}) {
  const [open, setOpen] = useState(false)
  const [type, setType] = useState(account.connection_type)
  const [serverPath, setServerPath] = useState(account.server_path)
  const [port, setPort] = useState(account.port)
  const [mailbox, setMailbox] = useState(account.mailbox)
  const [username, setUsername] = useState(account.username)
  const [password, setPassword] = useState(account.has_password ? '********' : '')
  const [clientId, setClientId] = useState(account.app_client_id)
  const [tenantId, setTenantId] = useState(account.app_tenant_id)
  const [secretKey, setSecretKey] = useState(account.app_secret_key)
  const [activate, setActivate] = useState(account.activate === 1)
  const [restrict, setRestrict] = useState(account.enable_sender_email === 1)
  const [body, setBody] = useState(account.email_body)
  const [error, setError] = useState('')

  useEffect(() => {
    setType(account.connection_type)
    setServerPath(account.server_path)
    setPort(account.port)
    setMailbox(account.mailbox)
    setUsername(account.username)
    setPassword(account.has_password ? '********' : '')
    setClientId(account.app_client_id)
    setTenantId(account.app_tenant_id)
    setSecretKey(account.app_secret_key)
    setActivate(account.activate === 1)
    setRestrict(account.enable_sender_email === 1)
    setBody(account.email_body)
  }, [account])

  const save = useMutation({
    mutationFn: () =>
      saveParsingAccount(account.ID, {
        connection_type: type,
        server_path: serverPath,
        port,
        mailbox,
        parsing_username: username,
        parsing_password: password,
        app_client_id: clientId,
        app_tenant_id: tenantId,
        app_secret_key: secretKey,
        activate_parsing: activate,
        enable_sender_email: restrict,
        email_body: body,
      }),
    onSuccess: (answer) => {
      setError('')
      if (answer.cron === 'failed') {
        toast(`Parsing settings saved, but the mail parsing cron was not updated. ${answer.cron_message || 'The cron server did not answer.'}`, 'Warning', 'warning')
      } else {
        toast('Parsing settings saved', 'Success')
      }
      onReload()
    },
    onError: (e) => {
      const message = e instanceof ApiError && e.message ? e.message : 'Something went wrong.'
      setError(message)
      toast('Something went wrong.', 'Error', 'error')
    },
  })

  const remove = useMutation({
    mutationFn: () => removeParsingAccount(account.ID),
    onSuccess: (answer) => {
      if (answer.cron === 'failed') {
        toast(`Account removed, but the mail parsing cron was not updated. ${answer.cron_message || 'The cron server did not answer.'}`, 'Warning', 'warning')
      }
      onReload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <div className="panel panel-default">
      <div className="panel-heading" role="tab">
        <div className="panel-title font-14">
          <a
            style={{ padding: 0, display: 'inline-block', cursor: 'pointer' }}
            role="button"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <h5>
              <i className="far fa-envelope-open" aria-hidden="true"></i>#{index + 1} Account
            </h5>
          </a>
          <button
            type="button"
            className="pull-right btn btn-sm btn-danger remove-parsing-account"
            data-account={account.ID}
            style={{ marginTop: '-4px' }}
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Remove
          </button>
        </div>
      </div>
      {open && (
        <div className="panel-collapse parse-settings-collapse">
          <div className="panel-body">
            <form
              className="parsingSettingsForm"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="form-group row">
                <div className="col-md-3">
                  <select
                    className="form-control form-control-sm connType"
                    name="connection_type"
                    value={type}
                    onChange={(e) => {
                      setError('')
                      setType(Number(e.target.value))
                    }}
                  >
                    <option value={1}>Imap Connection</option>
                    <option value={2}>Microsoft Graph Connection</option>
                  </select>
                </div>
              </div>

              <div className="form-group row">
                {type === 1 && (
                  <>
                    <div className="col-md-4 iconn">
                      <input
                        className="form-control input-sm"
                        type="text"
                        name="server_path"
                        value={serverPath}
                        onChange={(e) => setServerPath(e.target.value)}
                      />
                      <span className="help-block">Server path</span>
                    </div>
                    <div className="col-md-2 iconn">
                      <input
                        className="form-control input-sm"
                        type="text"
                        name="port"
                        value={port}
                        onChange={(e) => setPort(e.target.value)}
                      />
                      <span className="help-block">
                        Port. Default is <i className="highlights">143</i>
                      </span>
                    </div>
                  </>
                )}
                {type === 2 && (
                  <>
                    <div className="col-md-2 mgconn">
                      <input
                        className="form-control input-sm"
                        type="text"
                        name="app_client_id"
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                      />
                      <span className="help-block">Application Client Id</span>
                    </div>
                    <div className="col-md-2 mgconn">
                      <input
                        className="form-control input-sm"
                        type="text"
                        name="app_tenant_id"
                        value={tenantId}
                        onChange={(e) => setTenantId(e.target.value)}
                      />
                      <span className="help-block">Application Tenant Id</span>
                    </div>
                    <div className="col-md-2 mgconn">
                      <input
                        className="form-control input-sm"
                        type="text"
                        name="app_secret_key"
                        value={secretKey}
                        onChange={(e) => setSecretKey(e.target.value)}
                      />
                      <span className="help-block">Application Secret Key</span>
                    </div>
                  </>
                )}
                <div className="col-md-2">
                  <input
                    className="form-control input-sm"
                    type="text"
                    name="mailbox"
                    value={mailbox}
                    onChange={(e) => setMailbox(e.target.value)}
                  />
                  <span className="help-block">
                    Mailbox. Default is <i className="highlights">INBOX</i>
                  </span>
                </div>
              </div>

              <div className="form-group row">
                <div className="col-md-4">
                  <input
                    className="form-control input-sm"
                    placeholder="example@domain.com"
                    type="text"
                    name="parsing_username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                  <span className="help-block">Username or email address</span>
                </div>
                <div className="col-md-4">
                  <input
                    className="form-control input-sm"
                    placeholder="password"
                    type="password"
                    name="parsing_password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <span className="help-block">
                    Password{account.has_password ? ' (leave as it is to keep the current one)' : ''}
                  </span>
                </div>
              </div>

              <div className="form-group row">
                <div className="col-md-4">
                  <input
                    className="enable_parsing"
                    type="checkbox"
                    name="activate_parsing"
                    checked={activate}
                    onChange={(e) => setActivate(e.target.checked)}
                  />{' '}
                  <label style={{ color: '#6f7b8a' }}>Activate email parsing</label>
                </div>
                <div className="col-md-4">
                  <input
                    className="enable_sender_email"
                    type="checkbox"
                    name="enable_sender_email"
                    checked={restrict}
                    onChange={(e) => setRestrict(e.target.checked)}
                  />{' '}
                  <label style={{ color: '#6f7b8a' }}>Restrict foreign domains and notify sender</label>
                </div>
              </div>

              {restrict && (
                <div className="form-group row sender-email">
                  <div className="col-md-8">
                    <textarea
                      className="form-control input-sm"
                      name="email_body"
                      rows={6}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                    />
                    <span className="help-block">Email body</span>
                  </div>
                </div>
              )}

              {error !== '' && (
                <div className="alert alert-danger col-md-8 errResponse" role="alert">
                  {error}
                </div>
              )}

              <div className="aright">
                <button className="btn btn-sm btn-primary parsingSettingsButton" type="submit" disabled={save.isPending}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

/** Sectiunea "Email parsing (IMAP settings)". */
function ParsingSection() {
  const queryClient = useQueryClient()
  const list = useQuery(parsingAccountsQuery())

  const reload = () => {
    void queryClient.invalidateQueries({
      queryKey: ['settings', 'easeedesk', 'ticket_general_settings', 'parsing'],
    })
  }

  const add = useMutation({
    mutationFn: () => newParsingAccount(),
    onSuccess: reload,
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const accounts = list.data?.accounts ?? []

  return (
    <section className="settings">
      <div className="row">
        <div className="col-md-2">
          <h5 style={{ marginBottom: 20 }}>Email parsing (IMAP settings)</h5>
          <button
            type="button"
            className="btn btn-sm btn-success"
            id="new-parsing-account"
            disabled={add.isPending}
            onClick={() => add.mutate()}
          >
            <i className="fas fa-plus" aria-hidden="true"></i> New account
          </button>
        </div>
        <div className="col-md-10" id="parsing-accounts">
          {list.error ? (
            <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The accounts" />
          ) : !list.data ? (
            <Spinner />
          ) : accounts.length === 0 ? (
            <div className="alert alert-info">
              <i className="fas fa-info-circle" aria-hidden="true"></i> No settings available.
            </div>
          ) : (
            <div className="panel-group" style={{ margin: 0 }} role="tablist">
              {accounts.map((account, i) => (
                <ParsingAccountPanel key={account.ID} account={account} index={i} onReload={reload} />
              ))}
            </div>
          )}
        </div>
      </div>
      <hr className="dotted" />
    </section>
  )
}

/**
 * "Easeedesk configuration": butonul de refacere, cu rezultatul lui sub el.
 * Macheta veche: `#rebuildConfigurationForm` din `ticket_settings/index.php`.
 */
function RebuildSection() {
  const [result, setResult] = useState<RebuildResult | null>(null)

  const rebuild = useMutation({
    mutationFn: () => rebuildConfiguration(),
    onSuccess: (data) => {
      setResult(data)
      if (data.ok) {
        toast('Configuration build successfully.', 'Success')
      } else {
        toast(`Some parts were not rebuilt: ${data.errors.join(', ')}.`, 'Rebuild configuration', 'warning')
      }
    },
    onError: (e) => {
      setResult(null)
      toast(
        e instanceof ApiError && e.status === 403
          ? 'You do not have access to this action.'
          : e instanceof ApiError && e.message
            ? e.message
            : 'Something went wrong.',
        'Error',
        'error',
      )
    },
  })

  const done = result ? result.parts.filter((p) => p.ok).length : 0

  return (
    <section className="settings">
      <div className="row">
        <div className="col-md-2">
          <h5 style={{ marginBottom: 20 }}>Easeedesk configuration</h5>
        </div>
        <div className="col-md-10">
          <form
            id="rebuildConfigurationForm"
            onSubmit={(e) => {
              e.preventDefault()
              rebuild.mutate()
            }}
          >
            <div className="form-group row">
              <div className="col-md-12">
                <button type="submit" className="btn btn-sm btn-success" disabled={rebuild.isPending}>
                  <i className={rebuild.isPending ? 'fas fa-spinner fa-spin' : 'fas fa-cogs'} aria-hidden="true"></i>{' '}
                  {rebuild.isPending ? 'Rebuilding...' : 'Rebuild configuration'}
                </button>
                <span className="help-block">
                  Rebuild easeedesk configuration after every change to speed up the application and load new changes.
                  <br />
                  New tickets settings will take effect after you do the <strong>REBUILD</strong>.
                </span>
                {result && (
                  <div
                    className={`alert ${result.ok ? 'alert-success' : 'alert-warning'} rebuild-result`}
                    role="status"
                    style={{ marginTop: 10, marginBottom: 0 }}
                  >
                    {result.ok ? (
                      <>
                        <i className="fas fa-check" aria-hidden="true"></i> Configuration rebuilt: {done} of{' '}
                        {result.parts.length} parts.
                      </>
                    ) : (
                      <>
                        <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Rebuilt {done} of{' '}
                        {result.parts.length} parts. Not rebuilt: <strong>{result.errors.join(', ')}</strong>. The
                        other parts were saved; try again, and if it keeps failing, report it.
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
      <hr className="dotted" />
    </section>
  )
}

export function TicketGeneralSettingsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(ticketGeneralSettingsQuery())
  const [enabled, setEnabled] = useState<number[]>([])
  const [pairKey, setPairKey] = useState('')

  useEffect(() => {
    if (!shell.data) return
    setEnabled(shell.data.services.filter((s) => s.enabled === 1).map((s) => s.ID))
    setPairKey(shell.data.sms_pair_key)
  }, [shell.data])

  const reloadShell = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_general_settings'] })
  }

  const services = useMutation({
    mutationFn: () => saveServices(enabled),
    onSuccess: () => {
      toast('Services settings updated.', 'Services')
      reloadShell()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const sms = useMutation({
    mutationFn: () => saveSmsPairKey(pairKey),
    onSuccess: () => {
      toast('SMS settings updated.', 'SMS Conversation')
      reloadShell()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  const generate = useMutation({
    mutationFn: () => generatePairKey(),
    onSuccess: (data) => setPairKey(data.pair_key),
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  return (
    <div>
      <h4>
        <strong>General</strong> settings
      </h4>
      <hr />

      <RebuildSection />

      <ParsingSection />

      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ marginBottom: 20 }}>Services</h5>
          </div>
          <div className="col-md-10">
            <form
              id="update-services-form"
              onSubmit={(e) => {
                e.preventDefault()
                services.mutate()
              }}
            >
              <div className="form-group row">
                {shell.data.services.map((service) => (
                  <div className="col-md-3" key={service.ID}>
                    <input
                      className="service-checkbox"
                      type="checkbox"
                      name={`service[${service.ID}]`}
                      checked={enabled.includes(service.ID)}
                      onChange={(e) =>
                        setEnabled((prev) =>
                          e.target.checked ? [...prev, service.ID] : prev.filter((id) => id !== service.ID),
                        )
                      }
                    />{' '}
                    <label>{service.service_name}</label>
                  </div>
                ))}
              </div>
              <div className="aright">
                <button className="btn btn-primary btn-sm" type="submit" disabled={services.isPending}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
        <hr className="dotted" />
      </section>

      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ marginBottom: 20 }}>SMS Conversation</h5>
          </div>
          <div className="col-md-10">
            <form
              id="sms_settings_form"
              onSubmit={(e) => {
                e.preventDefault()
                sms.mutate()
              }}
            >
              <div className="form-group row">
                <div className="col-md-4">
                  <div className="input-group">
                    <div className="input-group-btn">
                      <button
                        type="button"
                        className="btn btn-default"
                        id="generate_key"
                        disabled={generate.isPending}
                        onClick={() => generate.mutate()}
                      >
                        <i className="fas fa-sync-alt" aria-hidden="true"></i> Generate
                      </button>
                    </div>
                    <input
                      className="form-control"
                      type="text"
                      id="pair_key"
                      name="pair_key"
                      value={pairKey}
                      onChange={(e) => setPairKey(e.target.value)}
                    />
                  </div>
                  <span className="help-block">Pair key</span>
                </div>
              </div>
              <div className="aright">
                <button className="btn btn-primary btn-sm" type="submit" disabled={sms.isPending}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
        <hr className="dotted" />
      </section>
    </div>
  )
}
